import React, { useState, useEffect, useRef , useCallback} from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '../../components/ui/dialog';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { getUser } from '../../utils/auth';
import api from '../../utils/api';
import { useVideoConsultation } from '../../hooks/useVideoConsultation';
import { ParticipantTile } from '../../components/VideoCallComponents';
import {
  Video,
  VideoOff,
  Mic,
  MicOff,
  Phone,
  PhoneOff,
  Monitor,
  MessageSquare,
  FileText,
  Upload,
  Send,
  Pill,
  Clock,
  User,
  Paperclip,
  Image,
  X,
  Download,
  Save,
  Plus,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Calendar,
  ArrowLeft,
  Search,
  Filter,
  Eye,
  FolderOpen,
  Mail,
  Phone as PhoneIcon,
  MapPin,
  CalendarDays,
  Heart,
  Droplets,
  Activity,
  Scale,
  Ruler,
  Loader2
} from 'lucide-react';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

// Mock scheduled consultations data with patient details and documents

export default function VideoConsultationPage() {
  const [user] = useState(getUser);
  const [view, setView] = useState('list'); // 'list' or 'call'
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Patient details and documents dialog state
  const [showPatientDetailsDialog, setShowPatientDetailsDialog] = useState(false);
  const [showDocumentsDialog, setShowDocumentsDialog] = useState(false);
  const [viewingPatient, setViewingPatient] = useState(null);
  const [patientDetailsLoading, setPatientDetailsLoading] = useState(false);
  const [patientDocsLoading, setPatientDocsLoading] = useState(false);
  const [loadedPatientDetails, setLoadedPatientDetails] = useState(null);
  const [loadedPatientDocuments, setLoadedPatientDocuments] = useState([]);

  // Daily.co video consultation hook
  const videoHook = useVideoConsultation(
    selectedConsultation?.id,
    selectedConsultation?.patientDetails?.user_id || selectedConsultation?.patientId
  );

  // Video call state
  const [roomName, setRoomName] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isMicOn, setIsMicOn] = useState(true);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [activeTab, setActiveTab] = useState('chat');
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  
  // Notes state
  const [notes, setNotes] = useState('');
  const [savedNotes, setSavedNotes] = useState([]);
  
  // Files state
  const [sharedFiles, setSharedFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Prescription state
  const [prescriptions, setPrescriptions] = useState([]);
  const [showPrescriptionDialog, setShowPrescriptionDialog] = useState(false);
  const [currentPrescription, setCurrentPrescription] = useState({
    medication: '',
    dosage: '',
    frequency: '',
    duration: '',
    instructions: ''
  });
  
  // Summary state
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);
  const [consultationSummary, setConsultationSummary] = useState({
    chiefComplaint: '',
    diagnosis: '',
    treatmentPlan: '',
    followUp: ''
  });

  const fileInputRef = useRef(null);
  const chatScrollRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);

  // Filter consultations
  const filteredConsultations = consultations.filter(c => {
    const matchesSearch = c.patientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         c.reason.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || c.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Timer for call duration
  useEffect(() => {
    let interval;
    if (isConnected || videoHook.isJoined) {
      interval = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isConnected, videoHook.isJoined]);


  const loadConsultations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/doctor/video-consultations');
      if (response.data.success) {
        setConsultations(response.data.consultations);
      }
    } catch (error) {
      toast.error('Failed to load consultations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConsultations();
  }, [loadConsultations]);

  // Auto-scroll chat
  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [messages]);

  const startConsultation = (consultation) => {
    setSelectedConsultation(consultation);
    setView('call');
    setCallDuration(0);
    setMessages([]);
    setSavedNotes([]);
    setSharedFiles([]);
    setPrescriptions([]);
    setConsultationSummary({
      chiefComplaint: '',
      diagnosis: '',
      treatmentPlan: '',
      followUp: ''
    });
  };

  const backToList = () => {
    if (isConnected || videoHook.isJoined) {
      toast.error('Please end the call before going back');
      return;
    }
    setView('list');
    setSelectedConsultation(null);
  };

  // Video call functions - Now using Daily.co!
  const startCall = async () => {
    if (!selectedConsultation) {
      toast.error('No consultation selected');
      return;
    }

    setIsConnecting(true);
    try {
      // Create Daily.co room for this consultation
      const patientId = selectedConsultation.patientDetails?.user_id || selectedConsultation.patientId;
      
      if (!patientId) {
        toast.error('Patient ID not found');
        setIsConnecting(false);
        return;
      }

      const roomData = await videoHook.createRoom();
      
      if (roomData) {
        setRoomName(roomData.room_name);
        
        // Start the Daily.co call
        await videoHook.startCall();
        
        setIsConnected(true);
        addMessage('system', 'Video consultation started via Daily.co. Real-time video enabled!');
        toast.success('Connected to video consultation');
      }
    } catch (error) {
      toast.error('Failed to start video call');
    } finally {
      setIsConnecting(false);
    }
  };

  const endCall = () => {
    setShowSummaryDialog(true);
  };

  const finalizeConsultation = async () => {
    try {
      // End Daily.co consultation
      if (roomName && videoHook.isJoined) {
        await videoHook.endConsultation(roomName);
      } else if (videoHook.isJoined) {
        await videoHook.leaveCall();
      }
      
      // Clean up local video if any
      if (localVideoRef.current?.srcObject) {
        localVideoRef.current.srcObject.getTracks().forEach(track => track.stop());
      }
      
      setIsConnected(false);
      setShowSummaryDialog(false);
      
      // Update consultation status
      setConsultations(prev => prev.map(c => 
        c.id === selectedConsultation.id ? { ...c, status: 'completed' } : c
      ));
      
      toast.success('Consultation ended and summary saved');
      setView('list');
      setSelectedConsultation(null);
      setRoomName(null);
    } catch (error) {
      // Still clean up even if there's an error
      setIsConnected(false);
      setShowSummaryDialog(false);
      setView('list');
      setSelectedConsultation(null);
      toast.warning('Consultation ended (with errors)');
    }
  };

  // Media control functions - proxy to Daily.co
  const toggleMic = () => {
    if (videoHook.isJoined) {
      videoHook.toggleAudio();
    }
    setIsMicOn(!isMicOn);
    toast.info(isMicOn ? 'Microphone muted' : 'Microphone unmuted');
  };

  const toggleVideo = () => {
    if (videoHook.isJoined) {
      videoHook.toggleVideo();
    }
    setIsVideoOn(!isVideoOn);
    toast.info(isVideoOn ? 'Camera turned off' : 'Camera turned on');
  };

  const toggleScreenShare = () => {
    if (videoHook.isJoined) {
      videoHook.toggleScreenShare();
    }
    setIsScreenSharing(!isScreenSharing);
    toast.info(isScreenSharing ? 'Screen sharing stopped' : 'Screen sharing started');
  };

  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatScheduledTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const isTomorrow = date.toDateString() === new Date(now.getTime() + 24 * 60 * 60000).toDateString();
    
    const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    
    if (isToday) return `Today at ${timeStr}`;
    if (isTomorrow) return `Tomorrow at ${timeStr}`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ` at ${timeStr}`;
  };

  // Chat functions
  const addMessage = (sender, text, type = 'text', file = null) => {
    setMessages(prev => [...prev, {
      id: Date.now(),
      sender,
      text,
      type,
      file,
      timestamp: new Date()
    }]);
  };

  const sendMessage = () => {
    if (!messageInput.trim()) return;
    addMessage('doctor', messageInput);
    setMessageInput('');
    
    // Simulate patient response
    setTimeout(() => {
      addMessage('patient', 'Thank you, doctor. I understand.');
    }, 2000);
  };

  // File sharing
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      toast.error('File size exceeds 5MB limit');
      return;
    }

    setUploadingFile(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const fileData = {
        id: Date.now(),
        name: file.name,
        size: file.size,
        type: file.type,
        url: URL.createObjectURL(file),
        uploadedAt: new Date(),
        uploadedBy: 'doctor'
      };
      
      setSharedFiles(prev => [...prev, fileData]);
      addMessage('doctor', `Shared file: ${file.name}`, 'file', fileData);
      toast.success('File shared successfully');
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Notes functions
  const saveNote = () => {
    if (!notes.trim()) return;
    setSavedNotes(prev => [...prev, {
      id: Date.now(),
      text: notes,
      timestamp: new Date()
    }]);
    setNotes('');
    toast.success('Note saved');
  };

  const deleteNote = (id) => {
    setSavedNotes(prev => prev.filter(note => note.id !== id));
  };

  // Prescription functions
  const addPrescription = () => {
    if (!currentPrescription.medication || !currentPrescription.dosage) {
      toast.error('Please fill in medication and dosage');
      return;
    }
    
    setPrescriptions(prev => [...prev, {
      id: Date.now(),
      ...currentPrescription
    }]);
    
    setCurrentPrescription({
      medication: '',
      dosage: '',
      frequency: '',
      duration: '',
      instructions: ''
    });
    
    setShowPrescriptionDialog(false);
    toast.success('Prescription added');
  };

  const deletePrescription = (id) => {
    setPrescriptions(prev => prev.filter(p => p.id !== id));
  };

  // View patient details - fetch from API if consultation has real ID
  const openPatientDetails = async (consultation) => {
    setViewingPatient(consultation);
    setShowPatientDetailsDialog(true);
    setLoadedPatientDetails(null);
    
    // Try to fetch from API if this is a real consultation with an ID
    if (consultation.id && !consultation.id.startsWith('mock-')) {
      setPatientDetailsLoading(true);
      try {
        const response = await api.get(`/doctor/consultations/${consultation.id}/patient`);
        setLoadedPatientDetails(response.data.patient);
      } catch (error) {
        // Fall back to mock data
        setLoadedPatientDetails(null);
      } finally {
        setPatientDetailsLoading(false);
      }
    }
  };

  // View patient documents - fetch from API if consultation has real ID
  const openPatientDocuments = async (consultation) => {
    setViewingPatient(consultation);
    setShowDocumentsDialog(true);
    setLoadedPatientDocuments([]);
    
    // Try to fetch from API if this is a real consultation with an ID
    if (consultation.id && !consultation.id.startsWith('mock-')) {
      setPatientDocsLoading(true);
      try {
        const response = await api.get(`/doctor/consultations/${consultation.id}/documents`);
        setLoadedPatientDocuments(response.data.documents || []);
      } catch (error) {
        // Fall back to mock data
        setLoadedPatientDocuments([]);
      } finally {
        setPatientDocsLoading(false);
      }
    }
  };

  // Save consultation notes to backend
  const saveConsultationNotes = async () => {
    if (!notes.trim()) return;
    
    const noteData = {
      id: Date.now(),
      text: notes,
      timestamp: new Date()
    };
    
    setSavedNotes(prev => [...prev, noteData]);
    
    // Try to save to backend if in an active consultation
    if (selectedConsultation?.id) {
      try {
        await api.post(`/doctor/consultations/${selectedConsultation.id}/notes`, { text: notes });
      } catch (error) {
      }
    }
    
    setNotes('');
    toast.success('Note saved');
  };

  // Save consultation summary and end call
  const saveConsultationSummary = async () => {
    // Try to save to backend
    if (selectedConsultation?.id) {
      try {
        await api.post(`/doctor/consultations/${selectedConsultation.id}/summary`, {
          ...consultationSummary,
          duration: callDuration,
          notes_count: savedNotes.length,
          prescriptions_count: prescriptions.length,
          files_count: sharedFiles.length
        });
        
        // Save prescriptions if any
        if (prescriptions.length > 0) {
          await api.post(`/doctor/consultations/${selectedConsultation.id}/prescriptions`, {
            medications: prescriptions
          });
        }
      } catch (error) {
      }
    }
    
    finalizeConsultation();
  };

  // Render consultation list view
  if (view === 'list') {
    return (
      <div className="space-y-6" data-testid="video-consultation-list">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Video className="w-6 h-6 text-teal-600" />
              Video Consultations
            </h1>
            <p className="text-gray-500 text-sm mt-1">Manage and conduct video consultations with patients</p>
          </div>
          <Badge className="bg-amber-100 text-amber-700 border-amber-200">
            <AlertCircle className="w-3 h-3 mr-1" />
            API Key Required for Live Calls
          </Badge>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Today's Consultations</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {consultations.filter(c => {
                      const d = new Date(c.scheduledTime);
                      return d.toDateString() === new Date().toDateString();
                    }).length}
                  </p>
                </div>
                <Calendar className="w-8 h-8 text-teal-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Upcoming</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {consultations.filter(c => c.status === 'scheduled').length}
                  </p>
                </div>
                <Clock className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Completed</p>
                  <p className="text-2xl font-bold text-green-600">
                    {consultations.filter(c => c.status === 'completed').length}
                  </p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Patients</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {new Set(consultations.map(c => c.patientName)).size}
                  </p>
                </div>
                <User className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by patient name or reason..."
              className="pl-10"
              data-testid="consultation-search"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant={filterStatus === 'all' ? 'default' : 'outline'}
              onClick={() => setFilterStatus('all')}
              size="sm"
              className={filterStatus === 'all' ? 'bg-teal-600 hover:bg-teal-700' : ''}
            >
              All
            </Button>
            <Button
              variant={filterStatus === 'scheduled' ? 'default' : 'outline'}
              onClick={() => setFilterStatus('scheduled')}
              size="sm"
              className={filterStatus === 'scheduled' ? 'bg-teal-600 hover:bg-teal-700' : ''}
            >
              Scheduled
            </Button>
            <Button
              variant={filterStatus === 'completed' ? 'default' : 'outline'}
              onClick={() => setFilterStatus('completed')}
              size="sm"
              className={filterStatus === 'completed' ? 'bg-teal-600 hover:bg-teal-700' : ''}
            >
              Completed
            </Button>
          </div>
        </div>

        {/* Consultations List */}
        <div className="space-y-4">
          {loading ? (
            <Card className="border shadow-sm">
              <CardContent className="p-12 text-center">
                <Loader2 className="w-12 h-12 text-blue-500 mx-auto mb-4 animate-spin" />
                <h3 className="text-lg font-semibold text-gray-700">Loading consultations...</h3>
                <p className="text-gray-500 text-sm mt-1">Please wait</p>
              </CardContent>
            </Card>
          ) : filteredConsultations.length === 0 ? (
            <Card className="border shadow-sm">
              <CardContent className="p-12 text-center">
                <Video className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-700">No consultations found</h3>
                <p className="text-gray-500 text-sm mt-1">Scheduled consultations will appear here</p>
              </CardContent>
            </Card>
          ) : (
            filteredConsultations.map((consultation) => (
              <Card key={consultation.id} className="border shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <Avatar className="h-14 w-14 border-2 border-teal-200">
                        <AvatarFallback className="bg-teal-100 text-teal-700 font-semibold text-lg">
                          {(consultation.patientName || 'P').split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <h3 className="font-semibold text-gray-900">{consultation.patientName || 'Patient'}</h3>
                        <p className="text-sm text-gray-500">{typeof consultation.reason === 'string' ? consultation.reason : 'Consultation'}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            <Clock className="w-3 h-3 mr-1" />
                            {consultation.duration || 30} min
                          </Badge>
                          <Badge className="text-xs bg-purple-100 text-purple-700">
                            {typeof consultation.type === 'string' ? consultation.type.replace('-', ' ') : 'video'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-col sm:items-end gap-2">
                      <div className="flex items-center gap-2">
                        <Badge 
                          className={`${
                            consultation.status === 'scheduled' ? 'bg-amber-100 text-amber-700' :
                            consultation.status === 'completed' ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {consultation.status === 'scheduled' ? (
                            <><Clock className="w-3 h-3 mr-1" /> {consultation.scheduledTime ? formatScheduledTime(consultation.scheduledTime) : 'Scheduled'}</>
                          ) : (
                            <><CheckCircle2 className="w-3 h-3 mr-1" /> Completed</>
                          )}
                        </Badge>
                      </div>
                      
                      {/* Documents badge */}
                      {consultation.documents && Array.isArray(consultation.documents) && consultation.documents.length > 0 && (
                        <Badge variant="outline" className="text-xs text-blue-600 border-blue-200">
                          <FolderOpen className="w-3 h-3 mr-1" />
                          {consultation.documents.length} document{consultation.documents.length > 1 ? 's' : ''} uploaded
                        </Badge>
                      )}
                      
                      {/* Action buttons */}
                      <div className="flex items-center gap-2 mt-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openPatientDetails(consultation)}
                          data-testid={`view-patient-${consultation.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View Details
                        </Button>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openPatientDocuments(consultation)}
                          className="text-blue-600 border-blue-200 hover:bg-blue-50"
                          data-testid={`view-docs-${consultation.id}`}
                        >
                          <FolderOpen className="w-4 h-4 mr-1" />
                          Documents
                        </Button>
                        
                        {consultation.status === 'scheduled' && (
                          <Button
                            onClick={() => startConsultation(consultation)}
                            className="bg-teal-600 hover:bg-teal-700 text-white"
                            data-testid={`start-consultation-${consultation.id}`}
                          >
                            <Video className="w-4 h-4 mr-2" />
                            Start Consultation
                          </Button>
                        )}
                        
                        {consultation.status === 'completed' && (
                          <Button variant="outline" size="sm">
                            <FileText className="w-4 h-4 mr-2" />
                            View Summary
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Patient Details Dialog */}
        <Dialog open={showPatientDetailsDialog} onOpenChange={setShowPatientDetailsDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-teal-600" />
                Patient Details
              </DialogTitle>
              <DialogDescription>
                Complete medical profile for {viewingPatient?.patientName}
              </DialogDescription>
            </DialogHeader>
            
            {/* Loading State */}
            {patientDetailsLoading && (
              <div className="py-12 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-teal-600 mx-auto mb-4" />
                <p className="text-gray-500">Loading patient details...</p>
              </div>
            )}
            
            {/* Patient Details - Use API data if available, otherwise mock data */}
            {!patientDetailsLoading && (viewingPatient?.patientDetails || loadedPatientDetails) && (() => {
              const details = loadedPatientDetails || viewingPatient?.patientDetails;
              const patientName = loadedPatientDetails?.full_name || viewingPatient?.patientName;
              
              return (
                <div className="space-y-6 py-4">
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                      <Avatar className="h-16 w-16 border-2 border-teal-200">
                        <AvatarFallback className="bg-teal-100 text-teal-700 font-bold text-xl">
                          {patientName?.split(' ').map(n => n[0]).join('') || 'P'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900">{patientName}</h3>
                        <p className="text-sm text-gray-500">{viewingPatient?.reason}</p>
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <Mail className="w-4 h-4 text-teal-600" />
                      Contact Information
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                        <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs text-gray-500">Email</p>
                          <p className="text-sm font-medium truncate">{details.email || 'Not provided'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                        <PhoneIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs text-gray-500">Phone</p>
                          <p className="text-sm font-medium">{details.phone || 'Not provided'}</p>
                        </div>
                      </div>
                      <div className="sm:col-span-2 flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                        <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs text-gray-500">Address</p>
                          <p className="text-sm font-medium">{details.address || 'Not provided'}</p>
                        </div>
                      </div>
                      {(details.emergencyContact || details.emergency_contact) && (
                        <div className="sm:col-span-2 flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                          <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-xs text-amber-600">Emergency Contact</p>
                            <p className="text-sm font-medium text-amber-800">
                              {typeof (details.emergencyContact || details.emergency_contact) === 'object' 
                                ? `${details.emergency_contact?.name || ''} - ${details.emergency_contact?.phone || ''}`
                                : (details.emergencyContact || details.emergency_contact)}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Physical Info */}
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <Activity className="w-4 h-4 text-teal-600" />
                      Physical Information
                    </h4>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="p-3 bg-gray-50 rounded-lg text-center">
                        <CalendarDays className="w-4 h-4 text-gray-400 mx-auto mb-1" />
                        <p className="text-xs text-gray-500">Age</p>
                        <p className="text-lg font-bold text-gray-900">{details.age || '-'}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg text-center">
                        <User className="w-4 h-4 text-gray-400 mx-auto mb-1" />
                        <p className="text-xs text-gray-500">Gender</p>
                        <p className="text-lg font-bold text-gray-900">{details.gender || '-'}</p>
                      </div>
                      <div className="p-3 bg-red-50 rounded-lg text-center">
                        <Droplets className="w-4 h-4 text-red-400 mx-auto mb-1" />
                        <p className="text-xs text-gray-500">Blood Group</p>
                        <p className="text-lg font-bold text-red-600">{details.bloodGroup || details.blood_group || '-'}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg text-center">
                        <Scale className="w-4 h-4 text-gray-400 mx-auto mb-1" />
                        <p className="text-xs text-gray-500">Weight</p>
                        <p className="text-lg font-bold text-gray-900">{details.weight || '-'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Medical History */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Allergies */}
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-red-500" />
                        Allergies
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {(details.allergies || []).length > 0 ? (
                          details.allergies.map((allergy, i) => (
                            <Badge key={i} className="bg-red-100 text-red-700">{allergy}</Badge>
                          ))
                        ) : (
                          <span className="text-sm text-gray-500">None recorded</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Chronic Conditions */}
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                        <Heart className="w-4 h-4 text-purple-500" />
                        Chronic Conditions
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {(details.chronicConditions || details.chronic_conditions || []).length > 0 ? (
                          (details.chronicConditions || details.chronic_conditions).map((condition, i) => (
                            <Badge key={i} className="bg-purple-100 text-purple-700">{condition}</Badge>
                          ))
                        ) : (
                          <span className="text-sm text-gray-500">None recorded</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Current Medications */}
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                      <Pill className="w-4 h-4 text-blue-500" />
                      Current Medications
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {(details.currentMedications || details.current_medications || []).length > 0 ? (
                        (details.currentMedications || details.current_medications).map((med, i) => (
                          <Badge key={i} variant="outline" className="text-blue-600 border-blue-200">{med}</Badge>
                        ))
                      ) : (
                        <span className="text-sm text-gray-500">None recorded</span>
                      )}
                    </div>
                  </div>

                  {/* Previous Surgeries */}
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                      <Activity className="w-4 h-4 text-teal-500" />
                      Previous Surgeries
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {(details.previousSurgeries || details.previous_surgeries || []).length > 0 ? (
                        (details.previousSurgeries || details.previous_surgeries).map((surgery, i) => (
                          <Badge key={i} variant="outline" className="text-teal-600 border-teal-200">{surgery}</Badge>
                        ))
                      ) : (
                        <span className="text-sm text-gray-500">None recorded</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPatientDetailsDialog(false)}>Close</Button>
              <Button 
                onClick={() => {
                  setShowPatientDetailsDialog(false);
                  openPatientDocuments(viewingPatient);
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <FolderOpen className="w-4 h-4 mr-2" />
                View Documents
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Patient Documents Dialog */}
        <Dialog open={showDocumentsDialog} onOpenChange={setShowDocumentsDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FolderOpen className="w-5 h-5 text-blue-600" />
                Patient Documents
              </DialogTitle>
              <DialogDescription>
                Medical documents uploaded by {viewingPatient?.patientName}
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              {/* Loading State */}
              {patientDocsLoading && (
                <div className="py-8 text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
                  <p className="text-gray-500">Loading patient documents...</p>
                </div>
              )}
              
              {/* Documents List - Use API data if available, otherwise mock data */}
              {!patientDocsLoading && (() => {
                const documents = loadedPatientDocuments.length > 0 ? loadedPatientDocuments : (viewingPatient?.documents || []);
                
                return documents.length > 0 ? (
                  <div className="space-y-3">
                    {documents.map((doc) => (
                      <div 
                        key={doc.id || doc.doc_id} 
                        className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors gap-3"
                      >
                        <div className="flex items-center gap-3 sm:gap-4">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 text-sm sm:text-base truncate">{doc.name}</p>
                            <div className="flex flex-wrap items-center gap-1 sm:gap-2 text-xs sm:text-sm text-gray-500">
                              <Badge variant="outline" className="text-xs">{doc.type || doc.category}</Badge>
                              <span className="hidden sm:inline">•</span>
                              <span>{doc.size}</span>
                              <span className="hidden sm:inline">•</span>
                              <span className="hidden sm:inline">Uploaded {doc.uploadedAt || doc.uploaded_at}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-auto sm:ml-0">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => toast.info(`Opening ${doc.name}...`)}
                          >
                            <Eye className="w-4 h-4 sm:mr-1" />
                            <span className="hidden sm:inline">View</span>
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => toast.success(`Downloading ${doc.name}...`)}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No documents uploaded by this patient</p>
                  </div>
                );
              })()}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDocumentsDialog(false)}>Close</Button>
              <Button 
                onClick={() => {
                  setShowDocumentsDialog(false);
                  openPatientDetails(viewingPatient);
                }}
                className="bg-teal-600 hover:bg-teal-700"
              >
                <User className="w-4 h-4 mr-2" />
                View Patient Details
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Render video call view
  return (
    <div className="h-[calc(100vh-120px)] flex flex-col bg-gray-900 rounded-xl overflow-hidden" data-testid="video-consultation-call">
      {/* Top Bar */}
      <div className="bg-gray-800 border-b border-gray-700 px-2 sm:px-4 py-2 sm:py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 sm:gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={backToList}
            className="text-gray-300 hover:text-white hover:bg-gray-700 px-2 sm:px-3"
            disabled={isConnected}
            data-testid="back-to-list"
          >
            <ArrowLeft className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Back</span>
          </Button>
          <Separator orientation="vertical" className="h-6 bg-gray-600 hidden sm:block" />
          <div className="flex items-center gap-2">
            <Video className="w-4 h-4 sm:w-5 sm:h-5 text-teal-400" />
            <span className="font-semibold text-white text-sm sm:text-base hidden sm:inline">Video Consultation</span>
          </div>
          {isConnected && (
            <Badge className="bg-green-600 text-white text-xs">
              <Clock className="w-3 h-3 mr-1" />
              {formatDuration(callDuration)}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-gray-300 border-gray-600 text-xs sm:text-sm">
            <User className="w-3 h-3 mr-1" />
            <span className="truncate max-w-[80px] sm:max-w-[150px]">{selectedConsultation?.patientName || 'Patient'}</span>
          </Badge>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Video Area */}
        <div className="flex-1 flex flex-col min-h-0">
          {/* Video Grid */}
          <div className="flex-1 p-2 sm:p-4 grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 overflow-auto">
            {/* Remote Video (Patient) - Using Daily.co */}
            {videoHook.isJoined && videoHook.callObject ? (
              <>
                {/* Remote participants */}
                {videoHook.participantIds
                  ?.filter(id => id !== videoHook.localParticipant?.session_id)
                  .map(id => (
                    <ParticipantTile 
                      key={id} 
                      callObject={videoHook.callObject}
                      participantId={id} 
                      isLocal={false} 
                    />
                  ))}
                
                {/* Local participant (doctor) */}
                {videoHook.localParticipant?.session_id && (
                  <ParticipantTile 
                    callObject={videoHook.callObject}
                    participantId={videoHook.localParticipant.session_id} 
                    isLocal={true} 
                  />
                )}
              </>
            ) : (
              <>
                {/* Placeholder when not connected */}
                <div className="relative bg-gray-800 rounded-xl overflow-hidden min-h-[180px] sm:min-h-[200px]">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-gray-400">
                      <Video className="w-10 h-10 sm:w-16 sm:h-16 mx-auto mb-2 sm:mb-4 opacity-50" />
                      <p className="text-sm">Waiting for connection...</p>
                    </div>
                  </div>
                  <div className="absolute bottom-2 sm:bottom-3 left-2 sm:left-3 bg-black/50 px-2 py-1 rounded text-white text-xs sm:text-sm">
                    {selectedConsultation?.patientName || 'Patient'}
                  </div>
                </div>

                {/* Local placeholder */}
                <div className="relative bg-gray-800 rounded-xl overflow-hidden min-h-[180px] sm:min-h-[200px]">
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-700 to-gray-800">
                    <div className="text-center">
                      <Avatar className="h-16 w-16 sm:h-24 sm:w-24 mx-auto mb-2 sm:mb-4">
                        <AvatarFallback className="bg-blue-600 text-white text-lg sm:text-2xl">
                          {user?.full_name?.split(' ').map(n => n[0]).join('') || 'DR'}
                        </AvatarFallback>
                      </Avatar>
                      <p className="text-white font-medium text-sm sm:text-base">Dr. {user?.full_name || 'You'}</p>
                      <p className="text-gray-400 text-xs sm:text-sm">Not connected</p>
                    </div>
                  </div>
                  <div className="absolute bottom-2 sm:bottom-3 left-2 sm:left-3 bg-black/50 px-2 py-1 rounded text-white text-xs sm:text-sm">
                    You (Dr. {user?.full_name || 'Doctor'})
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Call Controls */}
          <div className="bg-gray-800 border-t border-gray-700 p-2 sm:p-4">
            <div className="flex items-center justify-center gap-2 sm:gap-4">
              {!isConnected ? (
                <Button
                  onClick={startCall}
                  disabled={isConnecting}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 sm:px-8"
                  data-testid="start-call-btn"
                >
                  {isConnecting ? (
                    <><span className="animate-spin mr-2">⏳</span> Connecting...</>
                  ) : (
                    <><Phone className="w-5 h-5 mr-2" /> Start Call</>
                  )}
                </Button>
              ) : (
                <>
                  <Button
                    onClick={toggleMic}
                    variant={isMicOn ? 'outline' : 'destructive'}
                    size="lg"
                    className="rounded-full w-10 h-10 sm:w-14 sm:h-14"
                    data-testid="toggle-mic-btn"
                  >
                    {isMicOn ? <Mic className="w-4 h-4 sm:w-5 sm:h-5" /> : <MicOff className="w-4 h-4 sm:w-5 sm:h-5" />}
                  </Button>
                  
                  <Button
                    onClick={toggleVideo}
                    variant={isVideoOn ? 'outline' : 'destructive'}
                    size="lg"
                    className="rounded-full w-10 h-10 sm:w-14 sm:h-14"
                    data-testid="toggle-video-btn"
                  >
                    {isVideoOn ? <Video className="w-4 h-4 sm:w-5 sm:h-5" /> : <VideoOff className="w-4 h-4 sm:w-5 sm:h-5" />}
                  </Button>
                  
                  <Button
                    onClick={toggleScreenShare}
                    variant={isScreenSharing ? 'default' : 'outline'}
                    size="lg"
                    className="rounded-full w-10 h-10 sm:w-14 sm:h-14 hidden sm:flex"
                    data-testid="toggle-screen-btn"
                  >
                    <Monitor className="w-4 h-4 sm:w-5 sm:h-5" />
                  </Button>
                  
                  <Button
                    onClick={endCall}
                    variant="destructive"
                    size="lg"
                    className="rounded-full w-10 h-10 sm:w-14 sm:h-14 bg-red-600 hover:bg-red-700"
                    data-testid="end-call-btn"
                  >
                    <PhoneOff className="w-4 h-4 sm:w-5 sm:h-5" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Side Panel - Hidden on mobile, shown on lg+ */}
        <div className="hidden lg:flex w-80 xl:w-96 bg-white border-l border-gray-200 flex-col">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
            <TabsList className="grid grid-cols-4 m-2">
              <TabsTrigger value="chat" className="text-xs" data-testid="chat-tab">
                <MessageSquare className="w-4 h-4" />
              </TabsTrigger>
              <TabsTrigger value="notes" className="text-xs" data-testid="notes-tab">
                <FileText className="w-4 h-4" />
              </TabsTrigger>
              <TabsTrigger value="files" className="text-xs" data-testid="files-tab">
                <Paperclip className="w-4 h-4" />
              </TabsTrigger>
              <TabsTrigger value="prescription" className="text-xs" data-testid="prescription-tab">
                <Pill className="w-4 h-4" />
              </TabsTrigger>
            </TabsList>

            {/* Chat Tab */}
            <TabsContent value="chat" className="flex-1 flex flex-col m-0 p-0">
              <div className="px-3 py-2 border-b">
                <h3 className="font-semibold text-sm">Chat</h3>
              </div>
              <ScrollArea className="flex-1 p-3" ref={chatScrollRef}>
                <div className="space-y-3">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender === 'doctor' ? 'justify-end' : msg.sender === 'system' ? 'justify-center' : 'justify-start'}`}
                    >
                      {msg.sender === 'system' ? (
                        <div className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                          {msg.text}
                        </div>
                      ) : (
                        <div
                          className={`max-w-[80%] rounded-lg px-3 py-2 ${msg.sender === 'doctor' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-900'}`}
                        >
                          {msg.type === 'file' && msg.file ? (
                            <div className="flex items-center gap-2">
                              <Paperclip className="w-4 h-4" />
                              <span className="text-sm">{msg.file.name}</span>
                            </div>
                          ) : (
                            <p className="text-sm">{msg.text}</p>
                          )}
                          <p className={`text-xs mt-1 ${msg.sender === 'doctor' ? 'text-teal-200' : 'text-gray-500'}`}>
                            {msg.timestamp.toLocaleTimeString()}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
              <div className="p-3 border-t">
                <div className="flex gap-2">
                  <Input
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    placeholder="Type a message..."
                    className="flex-1"
                    data-testid="chat-input"
                  />
                  <Button onClick={sendMessage} size="sm" className="bg-teal-600 hover:bg-teal-700" data-testid="send-message-btn">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* Notes Tab */}
            <TabsContent value="notes" className="flex-1 flex flex-col m-0 p-0">
              <div className="px-3 py-2 border-b">
                <h3 className="font-semibold text-sm">Consultation Notes</h3>
              </div>
              <div className="p-3 border-b">
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Type your notes here..."
                  rows={4}
                  className="mb-2"
                  data-testid="notes-textarea"
                />
                <Button onClick={saveNote} size="sm" className="w-full bg-teal-600 hover:bg-teal-700" data-testid="save-note-btn">
                  <Save className="w-4 h-4 mr-2" /> Save Note
                </Button>
              </div>
              <ScrollArea className="flex-1 p-3">
                <div className="space-y-3">
                  {savedNotes.length === 0 ? (
                    <p className="text-center text-gray-500 text-sm py-4">No notes yet</p>
                  ) : (
                    savedNotes.map((note) => (
                      <div key={note.id} className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.text}</p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-500">
                            {note.timestamp.toLocaleTimeString()}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteNote(note.id)}
                            className="text-red-500 hover:text-red-700 h-6 px-2"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            {/* Files Tab */}
            <TabsContent value="files" className="flex-1 flex flex-col m-0 p-0">
              <div className="px-3 py-2 border-b">
                <h3 className="font-semibold text-sm">Shared Files</h3>
                <p className="text-xs text-gray-500">Max 5MB per file</p>
              </div>
              <div className="p-3 border-b">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  className="hidden"
                  accept="image/*,.pdf,.doc,.docx"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingFile}
                  className="w-full bg-teal-600 hover:bg-teal-700"
                  size="sm"
                  data-testid="upload-file-btn"
                >
                  {uploadingFile ? (
                    <><span className="animate-spin mr-2">⏳</span> Uploading...</>
                  ) : (
                    <><Upload className="w-4 h-4 mr-2" /> Upload File</>
                  )}
                </Button>
              </div>
              <ScrollArea className="flex-1 p-3">
                <div className="space-y-2">
                  {sharedFiles.length === 0 ? (
                    <p className="text-center text-gray-500 text-sm py-4">No files shared yet</p>
                  ) : (
                    sharedFiles.map((file) => (
                      <div key={file.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                        {file.type.startsWith('image/') ? (
                          <Image className="w-8 h-8 text-blue-500" />
                        ) : (
                          <FileText className="w-8 h-8 text-gray-500" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{file.name}</p>
                          <p className="text-xs text-gray-500">
                            {(file.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(file.url, '_blank')}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            {/* Prescription Tab */}
            <TabsContent value="prescription" className="flex-1 flex flex-col m-0 p-0">
              <div className="px-3 py-2 border-b">
                <h3 className="font-semibold text-sm">Prescriptions</h3>
              </div>
              <div className="p-3 border-b">
                <Button
                  onClick={() => setShowPrescriptionDialog(true)}
                  className="w-full bg-teal-600 hover:bg-teal-700"
                  size="sm"
                  data-testid="add-prescription-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Prescription
                </Button>
              </div>
              <ScrollArea className="flex-1 p-3">
                <div className="space-y-3">
                  {prescriptions.length === 0 ? (
                    <p className="text-center text-gray-500 text-sm py-4">No prescriptions added</p>
                  ) : (
                    prescriptions.map((rx) => (
                      <div key={rx.id} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-semibold text-blue-900">{rx.medication}</p>
                            <p className="text-sm text-blue-700">{rx.dosage}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deletePrescription(rx.id)}
                            className="text-red-500 hover:text-red-700 h-6 px-2"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                        <div className="mt-2 text-sm text-gray-600">
                          <p><strong>Frequency:</strong> {rx.frequency}</p>
                          <p><strong>Duration:</strong> {rx.duration}</p>
                          {rx.instructions && (
                            <p><strong>Instructions:</strong> {rx.instructions}</p>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Add Prescription Dialog */}
      <Dialog open={showPrescriptionDialog} onOpenChange={setShowPrescriptionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pill className="w-5 h-5 text-teal-600" />
              Add Prescription
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Medication *</Label>
                <Input
                  value={currentPrescription.medication}
                  onChange={(e) => setCurrentPrescription({...currentPrescription, medication: e.target.value})}
                  placeholder="e.g., Amoxicillin"
                  data-testid="prescription-medication"
                />
              </div>
              <div className="space-y-2">
                <Label>Dosage *</Label>
                <Input
                  value={currentPrescription.dosage}
                  onChange={(e) => setCurrentPrescription({...currentPrescription, dosage: e.target.value})}
                  placeholder="e.g., 500mg"
                  data-testid="prescription-dosage"
                />
              </div>
              <div className="space-y-2">
                <Label>Frequency</Label>
                <Input
                  value={currentPrescription.frequency}
                  onChange={(e) => setCurrentPrescription({...currentPrescription, frequency: e.target.value})}
                  placeholder="e.g., 3 times daily"
                />
              </div>
              <div className="space-y-2">
                <Label>Duration</Label>
                <Input
                  value={currentPrescription.duration}
                  onChange={(e) => setCurrentPrescription({...currentPrescription, duration: e.target.value})}
                  placeholder="e.g., 7 days"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Special Instructions</Label>
              <Textarea
                value={currentPrescription.instructions}
                onChange={(e) => setCurrentPrescription({...currentPrescription, instructions: e.target.value})}
                placeholder="e.g., Take with food, avoid alcohol"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPrescriptionDialog(false)}>Cancel</Button>
            <Button onClick={addPrescription} className="bg-teal-600 hover:bg-teal-700" data-testid="confirm-prescription-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Prescription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Consultation Summary Dialog */}
      <Dialog open={showSummaryDialog} onOpenChange={setShowSummaryDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              Consultation Summary
            </DialogTitle>
            <DialogDescription>
              Please complete the consultation summary before ending the call.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Chief Complaint</Label>
              <Textarea
                value={consultationSummary.chiefComplaint}
                onChange={(e) => setConsultationSummary({...consultationSummary, chiefComplaint: e.target.value})}
                placeholder="Patient's main concern or reason for visit"
                rows={2}
                data-testid="summary-complaint"
              />
            </div>
            <div className="space-y-2">
              <Label>Diagnosis</Label>
              <Textarea
                value={consultationSummary.diagnosis}
                onChange={(e) => setConsultationSummary({...consultationSummary, diagnosis: e.target.value})}
                placeholder="Clinical diagnosis or impression"
                rows={2}
                data-testid="summary-diagnosis"
              />
            </div>
            <div className="space-y-2">
              <Label>Treatment Plan</Label>
              <Textarea
                value={consultationSummary.treatmentPlan}
                onChange={(e) => setConsultationSummary({...consultationSummary, treatmentPlan: e.target.value})}
                placeholder="Recommended treatment and medications"
                rows={2}
                data-testid="summary-treatment"
              />
            </div>
            <div className="space-y-2">
              <Label>Follow-up</Label>
              <Input
                value={consultationSummary.followUp}
                onChange={(e) => setConsultationSummary({...consultationSummary, followUp: e.target.value})}
                placeholder="e.g., Review in 2 weeks"
                data-testid="summary-followup"
              />
            </div>
            
            <Separator />
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="font-semibold text-lg">{savedNotes.length}</p>
                <p className="text-gray-500">Notes Saved</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="font-semibold text-lg">{prescriptions.length}</p>
                <p className="text-gray-500">Prescriptions</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="font-semibold text-lg">{sharedFiles.length}</p>
                <p className="text-gray-500">Files Shared</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSummaryDialog(false)}>Continue Call</Button>
            <Button onClick={finalizeConsultation} className="bg-red-600 hover:bg-red-700" data-testid="end-consultation-btn">
              <PhoneOff className="w-4 h-4 mr-2" /> End Consultation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
